import type { ChatFileKind } from './chat-file-limits'

/**
 * How a chat file's kind is named for people, what it is served as, and whether it can be previewed in the chat
 * (#1734). The one contract the API and the portal both read, next to `chatFileKind`: the chip shows the label, the
 * server sends the media type, and only a previewable kind is served inline and offered a preview.
 */

/** Each supported kind as a person reads it. A Map, so a kind such as `constructor` names no label. */
const LABEL_BY_KIND: ReadonlyMap<string, string> = new Map<ChatFileKind, string>([
    ['png', 'PNG'],
    ['jpg', 'JPG'],
    ['pdf', 'PDF'],
    ['text', 'Text'],
    ['markdown', 'Markdown'],
    ['csv', 'CSV'],
    ['word', 'Word'],
    ['excel', 'Excel'],
])

/**
 * The kinds a browser can show safely inside the chat, images and PDF, with the media type each is served as. Every
 * other kind is opaque bytes: text and CSV are never given a type a browser would draw as a page.
 */
const PREVIEWABLE_MEDIA_TYPES: ReadonlyMap<string, string> = new Map<ChatFileKind, string>([
    ['png', 'image/png'],
    ['jpg', 'image/jpeg'],
    ['pdf', 'application/pdf'],
])

/** What every kind that is not previewable is served as. */
export const OPAQUE_CHAT_FILE_MEDIA_TYPE = 'application/octet-stream'

/** The kind of a chat file as a person reads it: `PNG`, `PDF`, `Word`…; `File` for a legacy file with no kind. */
export function chatFileKindLabel(kind: string | null | undefined): string {
    return (kind ? LABEL_BY_KIND.get(kind) : undefined) ?? 'File'
}

/** Whether a file of this kind can be previewed in the chat: png, jpg and pdf only. */
export function isChatFilePreviewable(kind: string | null | undefined): boolean {
    return kind ? PREVIEWABLE_MEDIA_TYPES.has(kind) : false
}

/**
 * The media type a chat file of this kind is served and shown as, from its stored kind and never from the caller:
 * `image/png`, `image/jpeg` or `application/pdf` for the previewable kinds, opaque bytes for every other.
 */
export function chatFileMediaType(kind: string | null | undefined): string {
    return (kind ? PREVIEWABLE_MEDIA_TYPES.get(kind) : undefined) ?? OPAQUE_CHAT_FILE_MEDIA_TYPE
}
